/**
 * The composer of "Edit with AI" (docs/plans/lms-course-editor-plan.md 8.2,
 * 6.2, 6.12, 9, 16, R6, R14; WP-E30): the instruction textarea with its
 * character count against the limit; the scope picker ("Whole course" or
 * "Only these sections", a checkbox list of the draft's sections); "Attach
 * documents" (the wizard's file and link inputs as a small list, with the
 * builder's 9.2 policy text and the attestation checkbox once anything is
 * attached); the estimate line from GET .../edits/estimate for the chosen
 * scope, asked again whenever the scope changes; the patient-information
 * notice; Send.
 *
 * Send first calls onBeforeSend (the editor flushes its autosave; a failed
 * save stops the send, the SectionActions rule), then posts JSON, or
 * multipart when anything is attached. A refusal shows its plain words
 * (editModel.ts) and keeps the text. Nothing here spends: the upper bound is
 * computed by the server from the draft's real size and the server re-checks
 * every rule and cap at Send (429 with the guard's code before anything is
 * written).
 */
import { useEffect, useId, useRef, useState } from 'react';
import { Link2, Loader2, Paperclip, Plus, Send, ShieldAlert, Trash2 } from 'lucide-react';
import { fieldCls, iconBtnCls, outlineBtnCls, primaryBtnCls, secondaryBtnCls, segBtnCls } from '../../../../../ui/styles';
import { BUILDER_FILE_ACCEPT } from '../../../../api/builder';
import { getEditEstimate, readEditorError, sendEditMessage } from '../../../../api/editor';
import type { EditEstimate, EditMessageAndRun, EditScope } from '../../../../api/editor';
import { noticeCls } from '../../../../components/player/styles';
import { ATTESTATION_LABEL, builderFileProblem, fileSizeLabel, linkProblem, UPLOAD_POLICY_TEXT } from '../builder/runModel';
import { Checkbox } from '../fields';
import { checkboxCls, hintCls, labelCls } from '../styles';
import {
  composerProblems,
  estimateLine,
  explainEditError,
  limitsOf,
  MATERIAL_ESTIMATE_NOTE,
  PATIENT_NOTICE,
  sendCapProblem,
} from './editModel';
import type { EditSectionRef } from './editModel';

export interface EditComposerProps {
  versionId: string;
  sections: EditSectionRef[];
  /** The composer is read-only (the draft is locked, or another message is working). */
  disabled?: boolean;
  /** Why it is read-only, shown above Send. */
  disabledReason?: string | null;
  /** A section to tick first in "Only these sections". */
  selectedSectionId?: string | null;
  /** The draft's message count: the estimate (and its limits block) is asked again when it moves. */
  messageCount: number;
  /** Flush the editor's autosave; false stops the send. */
  onBeforeSend: () => Promise<boolean>;
  /** The message was created. */
  onSent: (result: EditMessageAndRun) => void;
  /** The latest estimate (the panel shows the counts line from its limits). */
  onEstimate?: (estimate: EditEstimate | null) => void;
}

type ScopeKind = 'course' | 'sections';

export function EditComposer({
  versionId,
  sections,
  disabled = false,
  disabledReason = null,
  selectedSectionId = null,
  messageCount,
  onBeforeSend,
  onSent,
  onEstimate,
}: EditComposerProps) {
  const baseId = useId();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [text, setText] = useState('');
  const [kind, setKind] = useState<ScopeKind>('course');
  const [chosen, setChosen] = useState<string[]>(() =>
    selectedSectionId && sections.some((s) => s.id === selectedSectionId) ? [selectedSectionId] : []
  );
  const [attachOpen, setAttachOpen] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [links, setLinks] = useState<string[]>([]);
  const [linkDraft, setLinkDraft] = useState('');
  const [linkError, setLinkError] = useState<string | null>(null);
  const [attested, setAttested] = useState(false);
  const [estimate, setEstimate] = useState<EditEstimate | null>(null);
  const [estimateError, setEstimateError] = useState<string | null>(null);
  const [estimateTick, setEstimateTick] = useState(0);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const onEstimateRef = useRef(onEstimate);
  onEstimateRef.current = onEstimate;

  // Chosen ids in the draft's order, restricted to sections the draft still has.
  const chosenIds = sections.map((s) => s.id).filter((id) => chosen.includes(id));
  const scopeKey = kind === 'sections' && chosenIds.length > 0 ? chosenIds.join(',') : '';

  useEffect(() => {
    let cancelled = false;
    setEstimateError(null);
    const scope: EditScope | null = scopeKey ? { kind: 'sections', section_ids: scopeKey.split(',') } : null;
    getEditEstimate(versionId, scope)
      .then((e) => {
        if (cancelled) return;
        setEstimate(e);
        if (onEstimateRef.current) onEstimateRef.current(e);
      })
      .catch((err) => {
        if (cancelled) return;
        const problem = readEditorError(err, 'The cost could not be worked out.');
        setEstimate(null);
        setEstimateError(explainEditError(problem.code, problem.message, 'The cost could not be worked out.'));
        if (onEstimateRef.current) onEstimateRef.current(null);
      });
    return () => {
      cancelled = true;
    };
  }, [versionId, scopeKey, messageCount, estimateTick]);

  const limits = limitsOf(estimate);
  const trimmed = text.trim();
  const problems = composerProblems({
    text,
    maxChars: limits.maxChars,
    kind,
    chosen: chosenIds.length,
    files,
    links,
    maxFiles: limits.maxFiles,
    maxBytes: limits.maxBytes,
    attested,
  });
  const attached = files.length + links.length;
  const capProblem = attached > 0 ? null : sendCapProblem(estimate, kind, chosenIds.length);
  const estimateMatches = Boolean(
    estimate &&
      (kind === 'course' || chosenIds.length === 0
        ? !estimate.scope || estimate.scope.kind === 'course'
        : estimate.scope &&
          estimate.scope.kind === 'sections' &&
          Array.isArray(estimate.scope.section_ids) &&
          [...estimate.scope.section_ids].sort().join(',') === [...chosenIds].sort().join(','))
  );
  const line = estimateMatches ? estimateLine(estimate, kind, chosenIds.length) : null;
  const canSend = !disabled && !sending && problems.length === 0 && !capProblem && estimateMatches;
  const shownProblems = trimmed || attached > 0 ? problems : problems.filter((p) => p !== 'Write what to change.');

  const toggleSection = (id: string, on: boolean) => {
    setChosen((list) => (on ? (list.includes(id) ? list : [...list, id]) : list.filter((x) => x !== id)));
  };

  const addFiles = (list: FileList | null) => {
    if (!list || list.length === 0) return;
    const picked = Array.from(list);
    setFiles((prev) => [...prev, ...picked]);
    if (fileRef.current) fileRef.current.value = '';
  };

  const addLink = () => {
    const problem = linkProblem(linkDraft);
    if (problem) {
      setLinkError(problem);
      return;
    }
    const url = linkDraft.trim();
    if (links.includes(url)) {
      setLinkError('That address is already listed.');
      return;
    }
    setLinks((prev) => [...prev, url]);
    setLinkDraft('');
    setLinkError(null);
  };

  const send = async () => {
    if (!canSend) return;
    setError(null);
    setSending(true);
    try {
      let saved = false;
      try {
        saved = await onBeforeSend();
      } catch {
        saved = false;
      }
      if (!saved) {
        setError('The draft could not be saved first, so the message was not sent. Check the editor and try again.');
        return;
      }
      const result = await sendEditMessage(versionId, {
        instruction: trimmed,
        scope: kind === 'sections' ? { kind: 'sections', section_ids: chosenIds } : null,
        files,
        sourceLinks: links.map((url) => ({ url })),
        attestation: attested,
      });
      setText('');
      setFiles([]);
      setLinks([]);
      setLinkDraft('');
      setAttested(false);
      setAttachOpen(false);
      onSent(result);
    } catch (err) {
      const problem = readEditorError(err);
      setError(explainEditError(problem.code, problem.message));
    } finally {
      setSending(false);
    }
  };

  const overLimit = trimmed.length > limits.maxChars;

  return (
    <div className="space-y-3 border-t border-gray-200 pt-3 dark:border-gray-700" data-testid="lms-edit-composer">
      <fieldset className="min-w-0 space-y-3" disabled={disabled || sending}>
        <div className="space-y-1">
          <div className="flex items-center justify-between gap-2">
            <label htmlFor={`${baseId}-text`} className={labelCls}>
              What should change?
            </label>
            <span
              className={`text-xs tabular-nums ${overLimit ? 'text-red-700 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}
              data-testid="lms-edit-count"
            >
              {`${text.length.toLocaleString()} / ${limits.maxChars.toLocaleString()}`}
            </span>
          </div>
          <textarea
            id={`${baseId}-text`}
            rows={4}
            value={text}
            disabled={disabled || sending}
            onChange={(e) => setText(e.target.value)}
            placeholder="For example: Make every reading shorter and plainer."
            className={`${fieldCls} w-full disabled:cursor-not-allowed disabled:opacity-60`}
            data-testid="lms-edit-text"
          />
        </div>

        <div className="space-y-2">
          <div className={labelCls} id={`${baseId}-scope`}>
            What may it change?
          </div>
          <div
            role="group"
            aria-labelledby={`${baseId}-scope`}
            className="inline-flex items-center gap-0.5 rounded-lg border border-gray-300 bg-gray-100 p-0.5 dark:border-gray-600 dark:bg-gray-900/40"
          >
            <button type="button" className={segBtnCls(kind === 'course')} aria-pressed={kind === 'course'} onClick={() => setKind('course')} data-testid="lms-edit-scope-course">
              Whole course
            </button>
            <button
              type="button"
              className={segBtnCls(kind === 'sections')}
              aria-pressed={kind === 'sections'}
              onClick={() => setKind('sections')}
              data-testid="lms-edit-scope-sections"
            >
              Only these sections
            </button>
          </div>
          {kind === 'sections' ? (
            <ul className="max-h-48 space-y-0.5 overflow-y-auto rounded-lg border border-gray-200 p-2 dark:border-gray-700" data-testid="lms-edit-scope-list">
              {sections.map((s, i) => (
                <li key={s.id}>
                  <label className="flex min-h-[44px] cursor-pointer items-center gap-2 text-sm text-gray-800 dark:text-gray-200 sm:min-h-0 sm:py-0.5">
                    <input
                      type="checkbox"
                      className={checkboxCls}
                      checked={chosen.includes(s.id)}
                      onChange={(e) => toggleSection(s.id, e.target.checked)}
                      data-testid={`lms-edit-scope-section-${s.id}`}
                    />
                    <span className="min-w-0 truncate">{`${i + 1}. ${s.title || 'Untitled section'}`}</span>
                  </label>
                </li>
              ))}
            </ul>
          ) : null}
        </div>

        <div className="space-y-2">
          <button
            type="button"
            className={outlineBtnCls(attachOpen || attached > 0)}
            onClick={() => setAttachOpen((o) => !o)}
            aria-expanded={attachOpen}
            data-testid="lms-edit-attach-toggle"
          >
            <Paperclip className="h-4 w-4" aria-hidden="true" />
            {attached > 0 ? `Attach documents (${attached})` : 'Attach documents'}
          </button>
          {attachOpen ? (
            <div className="space-y-2 rounded-lg border border-gray-200 p-3 dark:border-gray-700" data-testid="lms-edit-attach">
              <div>
                <label htmlFor={`${baseId}-files`} className={labelCls}>
                  {`Documents (PDF, Word, text or Markdown; up to ${limits.maxFiles} documents and web links together)`}
                </label>
                <input
                  id={`${baseId}-files`}
                  ref={fileRef}
                  type="file"
                  multiple
                  accept={BUILDER_FILE_ACCEPT}
                  data-testid="lms-edit-file-input"
                  className="mt-1 block w-full text-sm text-gray-700 file:mr-3 file:min-h-[44px] file:rounded-lg file:border file:border-gray-300 file:bg-white file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-gray-700 hover:file:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-200 dark:file:border-gray-600 dark:file:bg-gray-700 dark:file:text-gray-200 sm:file:min-h-0"
                  onChange={(e) => addFiles(e.target.files)}
                />
              </div>
              <div className="grid gap-2 sm:grid-cols-[1fr_auto] sm:items-end">
                <div className="space-y-1">
                  <label htmlFor={`${baseId}-link`} className={labelCls}>
                    Web link (https)
                  </label>
                  <input
                    id={`${baseId}-link`}
                    type="url"
                    value={linkDraft}
                    onChange={(e) => {
                      setLinkDraft(e.target.value);
                      setLinkError(null);
                    }}
                    placeholder="https://"
                    className={`${fieldCls} min-h-[44px] w-full sm:min-h-0`}
                    data-testid="lms-edit-link-url"
                  />
                </div>
                <button type="button" className={secondaryBtnCls} onClick={addLink} data-testid="lms-edit-link-add">
                  <Plus className="h-4 w-4" aria-hidden="true" />
                  Add link
                </button>
              </div>
              {linkError ? (
                <p className="text-xs text-red-700 dark:text-red-400" role="alert" data-testid="lms-edit-link-error">
                  {linkError}
                </p>
              ) : null}
            </div>
          ) : null}
          {attached > 0 ? (
            <ul className="space-y-1" data-testid="lms-edit-attachments">
              {files.map((file, i) => {
                const problem = builderFileProblem(file, limits.maxBytes);
                return (
                  <li key={`f-${file.name}-${i}`} className="flex items-start justify-between gap-2 text-sm" data-testid={`lms-edit-file-${i}`}>
                    <div className="min-w-0">
                      <div className="truncate text-gray-900 dark:text-gray-100">{file.name}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{fileSizeLabel(file.size)}</div>
                      {problem ? <p className="text-xs text-red-700 dark:text-red-400">{problem}</p> : null}
                    </div>
                    <button
                      type="button"
                      className={iconBtnCls}
                      onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}
                      aria-label={`Remove ${file.name}`}
                      data-testid={`lms-edit-file-remove-${i}`}
                    >
                      <Trash2 className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </li>
                );
              })}
              {links.map((url, i) => (
                <li key={`l-${url}`} className="flex items-center justify-between gap-2 text-sm" data-testid={`lms-edit-link-${i}`}>
                  <span className="flex min-w-0 items-center gap-1.5 text-gray-800 dark:text-gray-200">
                    <Link2 className="h-4 w-4 flex-shrink-0 text-gray-400" aria-hidden="true" />
                    <span className="truncate font-mono text-xs">{url}</span>
                  </span>
                  <button
                    type="button"
                    className={iconBtnCls}
                    onClick={() => setLinks((prev) => prev.filter((_, j) => j !== i))}
                    aria-label={`Remove ${url}`}
                    data-testid={`lms-edit-link-remove-${i}`}
                  >
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          {attached > 0 ? (
            <div className="space-y-2">
              <div className={noticeCls('amber')} role="note" data-testid="lms-edit-policy">
                <ShieldAlert className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
                <span>{UPLOAD_POLICY_TEXT}</span>
              </div>
              <Checkbox label={ATTESTATION_LABEL} checked={attested} onChange={setAttested} testId="lms-edit-attest" />
            </div>
          ) : null}
        </div>
      </fieldset>

      <div className="space-y-1" aria-live="polite">
        {line ? (
          <p className="text-xs tabular-nums text-gray-700 dark:text-gray-300" data-testid="lms-edit-estimate">
            {line}
          </p>
        ) : estimateError ? (
          <p className="text-xs text-red-700 dark:text-red-400" data-testid="lms-edit-estimate-error">
            {`${estimateError} `}
            <button
              type="button"
              className="font-medium text-primary-700 underline-offset-2 hover:underline focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-primary-400"
              onClick={() => setEstimateTick((t) => t + 1)}
              data-testid="lms-edit-estimate-retry"
            >
              Try again
            </button>
          </p>
        ) : (
          <p className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400" data-testid="lms-edit-estimate-loading">
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
            Working out the cost...
          </p>
        )}
        {attached > 0 ? <p className={hintCls}>{MATERIAL_ESTIMATE_NOTE}</p> : null}
        {capProblem ? (
          <p className="text-xs text-red-700 dark:text-red-400" data-testid="lms-edit-cap-problem">
            {capProblem}
          </p>
        ) : null}
      </div>

      <p className="text-xs text-amber-800 dark:text-amber-300" data-testid="lms-edit-notice">
        {PATIENT_NOTICE}
      </p>

      {shownProblems.length > 0 ? (
        <ul className="space-y-0.5" data-testid="lms-edit-problems">
          {shownProblems.map((p) => (
            <li key={p} className="text-xs text-red-700 dark:text-red-400">
              {p}
            </li>
          ))}
        </ul>
      ) : null}

      {disabled && disabledReason ? (
        <p className={hintCls} data-testid="lms-edit-composer-locked">
          {disabledReason}
        </p>
      ) : null}

      {error ? (
        <div className={noticeCls('red')} role="alert" data-testid="lms-edit-send-error">
          {error}
        </div>
      ) : null}

      <div className="flex justify-end">
        <button type="button" className={primaryBtnCls} onClick={() => void send()} disabled={!canSend} data-testid="lms-edit-send">
          {sending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Send className="h-4 w-4" aria-hidden="true" />}
          Send
        </button>
      </div>
    </div>
  );
}

export default EditComposer;
